import {
  FeatureName,
  Product,
  useIsFeatureEnabled,
  useUpdateMutation,
} from '@gripp/shared-logic';
import {
  Colors,
  RoutineButtonData,
  RoutineSubButtons,
  SaveFormTop,
  Spacing,
  TopModalBar,
  useSystemNotification,
} from '@gripp/shared-ui';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { EditProductRoutineModalOld } from './editProductRoutineModal-old';

type EditProductRoutineModalProps = {
  product: Product;
  isVisible: boolean;
  onClose: () => void;
  productRoutines: RoutineButtonData[];
};

export const EditProductRoutineModal = (
  props: EditProductRoutineModalProps
) => {
  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  if (!isCropCircleRedesignEnabled) {
    return <EditProductRoutineModalOld {...props} />;
  }

  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  return <EditProductRoutineModalBase {...props} />;
};

export const EditProductRoutineModalBase = ({
  product,
  isVisible,
  onClose,
  productRoutines = [],
}: EditProductRoutineModalProps) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSystemNotification();

  const { mutateAsync: updateProduct } = useUpdateMutation({
    modelName: 'product',
    query: UPDATE_PRODUCT,
  });

  const [selectedRoutines, setSelectedRoutines] = useState<RoutineButtonData[]>(
    []
  );
  const [isDraggingRoutine, setIsDraggingRoutine] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize selected routines from productRoutines
  useEffect(() => {
    const selected = productRoutines.filter(
      (routine) => routine.isProductRoutine
    );
    setSelectedRoutines(selected);
    setHasChanges(false);
  }, [productRoutines, isVisible]);

  const onCancel = () => {
    onClose();
    // Reset state
    const selected = productRoutines.filter(
      (routine) => routine.isProductRoutine
    );
    setSelectedRoutines(selected);
    setHasChanges(false);
  };

  const saveRoutines = useCallback(
    async (routineData: RoutineButtonData[], showSuccessMessage = true) => {
      setHasChanges(true);

      const routineOrder = routineData.map((routine, i) => {
        return { order: i, workspaceRoutineId: routine.routineId };
      });

      const selectedRoutines = routineData.map((routine) => {
        return { id: routine.routineId };
      });

      const input = {
        id: product.id,
        routines: selectedRoutines,
        routineOrder: routineOrder,
      };

      try {
        await updateProduct({ input });
        return true;
      } catch (error) {
        showError(t('routines.editRoutines.saveError'));
        return false;
      }
    },
    [product.id, updateProduct, showError, t]
  );

  const onSubmit = async () => {
    await saveRoutines(selectedRoutines, true);
    onClose();
  };

  const isDirty = hasChanges;
  const isValid = true;

  return (
    <Modal
      visible={isVisible}
      animationType="fade"
      transparent={true}
      onRequestClose={onCancel}
    >
      <View style={styles.modalBackground}>
        <View style={styles.modalContainer}>
          <SaveFormTop
            title={t('routines.editRoutines.title')}
            saveLabel={t('topNav.save')}
            onSubmit={onSubmit}
            hasBorder={true}
            onCancel={onCancel}
            topBar={TopModalBar}
            isDirty={isDirty}
            isValid={isValid}
          >
            <SafeAreaView style={styles.contentContainer}>
              <ScrollView scrollEnabled={!isDraggingRoutine}>
                <Text style={styles.routineDescription}>
                  {t('routines.editRoutines.routineInWorkplace')}
                </Text>
                <RoutineSubButtons
                  uneditableButtons={[]}
                  routineButtons={productRoutines}
                  selectedRoutines={selectedRoutines}
                  setSelectedRoutines={setSelectedRoutines}
                  onSaveRoutines={saveRoutines}
                  showSuccess={showSuccess}
                  onDragStateChange={setIsDraggingRoutine}
                />
              </ScrollView>
            </SafeAreaView>
          </SaveFormTop>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalBackground: {
    backgroundColor: Colors.blackInactive,
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    width: 560,
    height: 650,
    borderRadius: 12,
    backgroundColor: Colors.primaryGrayBackground,
    overflow: 'hidden',
  },
  contentContainer: {
    flex: 1,
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingVertical: Spacing.basePadding.paddingVertical,
    marginBottom: 0,
    paddingBottom: 60,
    backgroundColor: Colors.primaryGrayBackground,
  },
  routineDescription: {
    paddingBottom: Spacing.basePadding.base / 2,
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 16,
  },
});

const UPDATE_PRODUCT = `
  mutation Mutation($input: MutateProductInput!) {
  updateProduct(input: $input) {
    id
  }
}
`;
